import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Menu, Search, Bell, LogOut, User, Settings, Plus } from 'lucide-react';
import ChatInbox from '../components/chat/ChatInbox';

export default function TopBar({ onMenuToggle }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hasIntake, setHasIntake] = useState(false);
  const menuRef = useRef(null);

  // Fetch record types to check for Intake — same pattern as Sidebar.js
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('lf_token');
    fetch('/api/admin/record-types', {
      headers: { 'Authorization': 'Bearer ' + token }
    })
    .then(r => r.json())
    .then(data => {
      const types = data.record_types || [];
      setHasIntake(types.some(rt => rt.slug === 'intake' && rt.is_system));
    })
    .catch(() => {});
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4">
      {/* Left: menu toggle + search */}
      <div className="flex items-center gap-4">
        <button onClick={onMenuToggle} className="text-gray-500 hover:text-gray-700 lg:hidden">
          <Menu className="h-6 w-6" />
        </button>

        {/* Global search - placeholder for Phase 4 */}
        <div className="hidden md:flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 w-80">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search records..."
            className="bg-transparent border-none outline-none text-sm text-gray-700 w-full placeholder-gray-400"
            disabled
          />
        </div>
      </div>

      {/* Right: new intake + chat + notifications + user menu */}
      <div className="flex items-center gap-3">
        {hasIntake && (
          <button
            onClick={() => navigate('/records/intake/new')}
            className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New Intake</span>
          </button>
        )}

        <ChatInbox />
        <button className="relative text-gray-400 hover:text-gray-600">
          <Bell className="h-5 w-5" />
        </button>

        {/* User dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 hover:bg-gray-50 rounded-lg px-2 py-1"
          >
            <div className="h-8 w-8 rounded-full bg-brand-600 flex items-center justify-center overflow-hidden">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xs font-medium text-white">{initials}</span>
              )}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-gray-700">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500">{user?.role_name || (user?.is_admin ? 'Admin' : 'Worker')}</p>
            </div>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <button
                onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <User className="h-4 w-4" /> Profile
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Settings className="h-4 w-4" /> Settings
              </button>
              <hr className="my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
